// Initialize window.ldConstants for gonfalon package compatibility
// Now uses dynamic flag retrieval from Gonfalon's internal API
import { getClient, resetClient } from '@gonfalon/openapi/client';
import { initializeAPIClient } from '@gonfalon/rest-api/client';
import type { Middleware } from 'openapi-fetch';

import config from './config';
import { flagService } from './services/flagService';
import { handleAuthRedirect } from './shared/authRedirect';
import { getStatusFromError } from './shared/utils';

type WindowWithLdConstants = Window & {
  ldConstants: { [key: string]: unknown };
};

declare const window: WindowWithLdConstants;

// Initial constants to bootstrap the system before dynamic flags are loaded
const bootstrapConstants = {
  // Use vitalstatistix configuration for gonfalon backend
  baseUri: (() => {
    const uri = config.gonfalon?.host || config.gonfalon?.publicUrl || window.location.origin;
    return uri;
  })(),
  clientSideId: config.launchdarkly?.clientId || '',
  frontendVersion: '1.0.0',
  backendVersion: '1.0.0',

  // Bootstrap with flags that enable all resource types for vitalstatistix audit log
  allClientSideFlags: {
    'enable-ai-bundle': true,
    'release-automation': true,
    'enable-views': true,
  },

  // Mock values for optional constants
  userId: null,
  userEmail: null,
  accountId: null,
  planInfo: null,
  subscriptionState: null,
};

// Initialize window.ldConstants immediately when this module is imported
if (!window.ldConstants) {
  window.ldConstants = bootstrapConstants;
}

// Vitalstatistix middleware for URL rewriting and authentication
const vitalstatistixMiddleware: Middleware = {
  onRequest({ request }: { request: Request }) {
    const url = new URL(request.url);

    // URL rewriting: redirect API calls to the configured Gonfalon host.
    // Production builds always rewrite (!__DEV__ short-circuits).
    // Dev builds only rewrite when targeting a remote environment (e.g. staging);
    // pure local dev relies on the dev-server proxy instead.
    const gonfalonBaseUrl = config.gonfalon?.host || config.gonfalon?.publicUrl;
    const isLocalGonfalon = !gonfalonBaseUrl || /localhost/i.test(gonfalonBaseUrl);
    if (
      (!__DEV__ || !isLocalGonfalon) &&
      gonfalonBaseUrl &&
      (url.pathname.startsWith('/internal/') ||
        url.pathname.startsWith('/private/') ||
        url.pathname.startsWith('/api/v2/'))
    ) {
      const gonfalonUrl = new URL(gonfalonBaseUrl);
      url.hostname = gonfalonUrl.hostname;
      url.port = gonfalonUrl.port;
      url.protocol = gonfalonUrl.protocol;
    }

    // Redirect internal billing subscription usage to private endpoint for cross-account access
    if (url.pathname === '/internal/billingv2/account/subscription/usage') {
      // Extract account ID with same priority as authentication section below
      let accountId = '68b8c1ebb1eda25033c8e918'; // Default test account

      // Priority 1: Check if AccountContextProvider has set the current account
      if (window.__VITAL_CURRENT_ACCOUNT__?._id) {
        accountId = window.__VITAL_CURRENT_ACCOUNT__._id;
      } else {
        // Priority 2: Extract from current URL
        const currentUrl = window.location.pathname;
        const accountMatch = currentUrl.match(/\/accounts\/([a-f0-9]{24})/);
        if (accountMatch) {
          [, accountId] = accountMatch;
        }
      }

      // Rewrite to private endpoint with account ID
      url.pathname = `/private/billingv2/accounts/${accountId}/subscription/usage`;
    }

    // Extract account ID for authentication headers
    let authAccountId = '68b8c1ebb1eda25033c8e918'; // Default test account

    // Priority 1: Check if AccountContextProvider has set the current account
    if (window.__VITAL_CURRENT_ACCOUNT__?._id) {
      authAccountId = window.__VITAL_CURRENT_ACCOUNT__._id;
    } else {
      // Priority 2: Extract from current URL
      const currentUrl = window.location.pathname;
      const accountMatch = currentUrl.match(/\/accounts\/([a-f0-9]{24})/);
      if (accountMatch) {
        [, authAccountId] = accountMatch;
      }
    }

    // Add authentication headers for internal/private/api v2 billing endpoints
    if (
      url.pathname.startsWith('/internal/') ||
      url.pathname.startsWith('/private/') ||
      url.pathname.startsWith('/api/v2/')
    ) {
      const headers = new Headers(request.headers);
      headers.set('X-LD-AccountId', authAccountId);
      headers.set('X-LD-EnvId', authAccountId);
      headers.set('X-LD-PrjId', authAccountId);

      const requestInit: RequestInit = {
        method: request.method,
        headers,
        referrer: request.referrer,
        referrerPolicy: request.referrerPolicy,
        mode: request.mode,
        credentials: 'include', // Use 'include' for cross-origin requests
        cache: request.cache,
        redirect: request.redirect,
        integrity: request.integrity,
      };

      // Only include body for methods that support it
      if (request.method !== 'GET' && request.method !== 'HEAD') {
        requestInit.body = request.body;
      }

      return new Request(url.toString(), requestInit);
    }

    return request;
  },
};

// Ensure Gonfalon's REST API client is initialized before any components render
try {
  initializeAPIClient(
    {
      onError: (error: unknown) => {
        // Filter out AbortError messages - these are normal React Query cleanup
        if (
          error instanceof Error &&
          (error.name === 'AbortError' ||
            error.message?.includes('signal is aborted') ||
            error.message?.includes('AbortError'))
        ) {
          return; // Don't log AbortErrors
        }

        // Check for 401 Unauthorized and redirect to login
        const status = getStatusFromError(error);
        handleAuthRedirect(status);

        // oxlint-disable-next-line no-console -- Important for debugging API errors
        console.error('Gonfalon REST API error:', error);
      },
    },
    { validate: false },
  );
} catch (e) {
  // oxlint-disable-next-line no-console -- Important for debugging initialization errors
  console.warn('initializeAPIClient in globals failed (likely already initialized):', e);
}

export const initializeGlobals = async (): Promise<void> => {
  // Initialize window.ldConstants with bootstrap values if not already set
  if (!window.ldConstants) {
    window.ldConstants = bootstrapConstants;
  }

  // Initialize the dynamic flag service to fetch real flags from Gonfalon
  try {
    await flagService.initialize();
  } catch (error) {
    // oxlint-disable-next-line no-console -- Important for debugging flag initialization errors
    console.error('Failed to initialize dynamic flags, falling back to bootstrap values:', error);
  }

  // Add our middleware to the OpenAPI client
  const client = getClient();
  client.use(vitalstatistixMiddleware);

  // Set up the reset client function for account switching
  if (typeof window !== 'undefined') {
    window.__GONFALON_RESET_CLIENT__ = () => {
      resetClient();
      // Re-add middleware after reset
      const newClient = getClient();
      newClient.use(vitalstatistixMiddleware);
    };
  }
};
